import React, { useState, useEffect } from 'react';
import useTranslation from 'Hooks/useTranslation';
import { Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material';
import { MoreVert, Notes } from '@mui/icons-material';
import { Meet } from 'Images';
import Alert from 'Components/Alert';
import Modal from 'Components/Modal';
import Options from 'Components/Options';
import FormValidations from 'Utils/FormValidations';
import { MEET } from 'Constants/General';
import { NO_EMPTY_NO_EMPTY_SPACES, SESSION_MEET_VALID_URL } from 'Constants/Regex';
import { colors } from 'Constants/Colors';
import TextInput from 'Components/FormComponents/TextInput';
import ModalInformation from '../../Components/ModalInformation';
import useModal from '../../Hooks/useModal';
import useLesson from '../../Hooks/useLesson';
import useSession from '../../Hooks/useSession';
import useUsers from '../../Hooks/useUsers';
import Title from '../../Components/Title';
import PrivateLessonForm from '../../Components/PrivateLesson';
import {
  nameStyle,
  FormContainer,
  DateContainer,
  DateOptions,
  dateStyle,
  timeStyle,
  IconTextContainer,
  iconTextField,
  iconTextArea,
} from './style';

function LessonMeet({
  addLessonSession,
  module,
  updateLesson,
  currentPositionLesson,
  deleteLesson,
  updateLessonSessionData,
  courseId,
  alert,
  setAlert,
}) {
  const lesson = module.lessons[currentPositionLesson];
  const [formData, setFormData] = useState({
    lessonName: lesson?.name || '',
    date: lesson?.date || '',
    time: lesson?.time?.slice(0, 5) || '',
    meetUrl: lesson?.meetUrl || '',
    description: lesson?.description || '',
    isDraft: true,
  });
  const [unchanged, setUnchanged] = useState(true);
  const [errors, setErrors] = useState();
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [allDayCheckbox, setAllDayCheckbox] = useState(lesson?.time === null);
  const { t2 } = useTranslation('EditCourse');
  const { openModal, setOpenModal } = useModal();
  const { users, enrolledUsers } = useUsers({ courseId, lessonId: lesson?.id });

  useEffect(() => {
    if (alert) {
      setAlertMessage(t2(`${alert}`));
      setAlertOpen(true);
      setTimeout(() => {
        setAlert('');
      }, 2000);
    }
  }, [alert]);

  const { isDraft, isPrivate, privateUsers, setPrivateUsers, setIsDraft, onDelete, setIsPrivate } = useLesson({
    module,
    updateLesson,
    lesson,
    deleteLesson,
  });

  const { onCreateSession, onUpdateSession } = useSession({
    addLessonSession,
    module,
    updateLesson,
    updateLessonSessionData,
    isDraft,
    isPrivate,
    privateUsers,
    setPrivateUsers,
  });

  const enableButton = () => {
    if (lesson?.id) return t2('SaveChanges');

    return t2('Save');
  };

  const handleModalAccept = async () => {
    setOpenModal(false);

    if (lesson?.id) {
      await onUpdateSession({ ...formData, id: lesson.id });

      return;
    }

    onCreateSession(formData, MEET);
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const handleChange = event => {
    setUnchanged(false);
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleCheckbox = async () => {
    setUnchanged(false);
    await setFormData({
      ...formData,
      time: '00:00',
    });
    setFormData({ ...formData, time: '' });
    setAllDayCheckbox(!allDayCheckbox);
  };

  const validate = () => {
    const temp = {};
    temp.lessonName = formData.lessonName.match(NO_EMPTY_NO_EMPTY_SPACES) ? t2('FormErrorSessionName') : '';

    // Validación si el día ya paso, ignorando la hora actual.
    temp.date =
      FormValidations.dayHasPassed(formData.date, {
        message: t2('FormErrorSessionDate'),
        ignoreTime: true,
      }) || '';

    // Validamos el tiempo si no es un evento de todo el día.
    const datetime = formData.time ? `${formData.date}T${formData.time}` : undefined;

    // Validamos si la fecha con hora ya paso.
    temp.time =
      FormValidations.datetimeHasPassed(datetime, {
        message: t2('FormErrorSessionTime'),
      }) || '';

    temp.meetUrl = formData.meetUrl.match(SESSION_MEET_VALID_URL) ? '' : t2('FormErrorSessionURL');

    if (allDayCheckbox) {
      temp.time = '';
    }
    setErrors({
      ...temp,
    });

    return Object.values(temp).every(x => x === '');
  };

  const checkValidation = () => {
    const isValid = validate();
    if (isValid) {
      setOpenModal(true);
    }
  };

  const handleUserSelect = array => {
    setUnchanged(false);
    setPrivateUsers([...array]);
  };

  return (
    <div>
      <Title
        name={t2('LessonMeet')}
        textButton={enableButton()}
        check={isDraft}
        setCheck={setIsDraft}
        setUnchanged={setUnchanged}
        checkText={t2('Draft')}
        checkInfo={t2('DraftCondition')}
        checkDisabled={false}
        setOpenModal={checkValidation}
        buttonDisabled={unchanged}
      >
        <Options Icon={<MoreVert />} disabledEffect>
          <MenuItem onClick={onDelete}>{t2('DeleteLesson')}</MenuItem>
        </Options>
      </Title>
      <FormContainer>
        <TextInput
          name="lessonName"
          label={t2('LabelRemoteSessionName')}
          color="MexlogGreen"
          value={formData?.lessonName}
          onChange={handleChange}
          style={nameStyle}
          errortext={errors?.lessonName}
        />
        <DateContainer>
          <TextInput
            name="date"
            label={t2('LabelSessionDate')}
            color="MexlogGreen"
            value={formData?.date}
            onChange={handleChange}
            style={dateStyle}
            errortext={errors?.date}
            type="date"
          />
          <TextInput
            name="time"
            label={t2('LabelSessionTimeStart')}
            color="MexlogGreen"
            value={formData?.time}
            onChange={handleChange}
            style={timeStyle}
            errortext={errors?.time}
            type="time"
            disabled={allDayCheckbox || formData?.date === ''}
          />
        </DateContainer>
        <DateOptions>
          <FormControlLabel
            name="allDay"
            control={<Checkbox checked={allDayCheckbox} onChange={handleCheckbox} color="success" />}
            label={t2('LabelSessionAllDay')}
          />
        </DateOptions>
        <IconTextContainer>
          <img src={Meet} />
          <TextInput
            name="meetUrl"
            label={t2('LabelRemoteSessionURL')}
            color="MexlogGreen"
            value={formData?.meetUrl}
            onChange={handleChange}
            style={iconTextField}
            errortext={errors?.meetUrl}
          />
        </IconTextContainer>
        <IconTextContainer>
          <Notes />
          <TextField
            id="description"
            name="description"
            value={formData?.description || ''}
            placeholder={t2('LabelSessionDescription')}
            multiline
            minRows={3}
            maxRows={4}
            onChange={handleChange}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                backgroundColor: colors.WHITE,
                width: '100%',
              },
            }}
            InputProps={{ disableunderline: 'true' }}
            color="success"
          />
        </IconTextContainer>
        <PrivateLessonForm
          isPrivate={isPrivate}
          setIsPrivate={setIsPrivate}
          users={users}
          privateUsers={privateUsers}
          selectHandler={handleUserSelect}
          enrolledUsers={enrolledUsers}
          setUnchanged={setUnchanged}
        />
      </FormContainer>
      <Modal
        open={openModal}
        setOpen={setOpenModal}
        Children={
          <ModalInformation accept={handleModalAccept} cancel={handleModalClose} title={t2('ModalTitleSession')} />
        }
      />
      <Alert message={alertMessage} open={alertOpen} setOpen={setAlertOpen} />
    </div>
  );
}

export default LessonMeet;
